import { useEffect, useRef, useState } from "react";
import { Loader2, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

const newToken = () =>
  (window.crypto?.randomUUID ? window.crypto.randomUUID() : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);

/** Address field backed by Google Places, proxied through our API. Falls back to plain text. */
export const AddressAutocomplete = ({ value, onChange, onResolved, placeholder, testId }) => {
  const [enabled, setEnabled] = useState(false);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const token = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/api/places/config").then(({ data }) => setEnabled(data.enabled)).catch(() => setEnabled(false));
    return () => clearTimeout(timer.current);
  }, []);

  const type = (text) => {
    onChange(text);
    setItems([]);
    if (!enabled || text.trim().length < 3) return;
    if (!token.current) token.current = newToken();
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.post("/api/places/autocomplete", { input: text, session_token: token.current });
        setItems(data.suggestions || []);
      } catch {
        setEnabled(false);
      } finally {
        setBusy(false);
      }
    }, 300);
  };

  const pick = async (item) => {
    setItems([]);
    setBusy(true);
    try {
      const { data } = await api.get(`/api/places/details/${encodeURIComponent(item.place_id)}`, {
        params: { session_token: token.current },
      });
      onChange(data.address || item.full_text);
      onResolved?.(data);
      token.current = null;
    } catch {
      onChange(item.full_text);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          value={value}
          onChange={(e) => type(e.target.value)}
          placeholder={placeholder || "Start typing an address…"}
          autoComplete="off"
          data-testid={testId}
        />
        {busy && <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-emerald-700" />}
      </div>
      {items.length > 0 && (
        <ul
          className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
          data-testid={`${testId}-suggestions`}
        >
          {items.map((item) => (
            <li key={item.place_id}>
              <button
                type="button"
                onMouseDown={() => pick(item)}
                className="flex w-full items-start gap-2 px-3 py-2 text-left hover:bg-emerald-50"
                data-testid={`${testId}-option-${item.place_id}`}
              >
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700" />
                <span className="min-w-0">
                  <span className="block truncate text-sm font-medium text-slate-900">{item.main_text}</span>
                  <span className="block truncate text-xs text-slate-500">{item.secondary_text}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}
      {!enabled && (
        <p className="mt-1.5 text-xs text-slate-500">Address search is unavailable — type the address in full.</p>
      )}
    </div>
  );
};
